import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Dymo from 'services/Dymo';
import DefaultButton from 'components/Utils/NewDefaultButton';
import './style.scss';
import { useAppSelector } from 'careand-redux/store';
import StaffUtils from 'services/StaffUtils';
import { PhysicianPhysicianTypeEnum } from 'generated/api';
import { IsTypeNP } from 'careand-redux/selectors';
import MenuItem from '@material-ui/core/MenuItem';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';

const LAB_ROLES = [PhysicianPhysicianTypeEnum.Phlebotomist, PhysicianPhysicianTypeEnum.Nurse];

const drugLabelSchema = z.object({
  id: z.string().optional().or(z.literal('')),
  name: z.string().optional().or(z.literal('')),
  dosage: z.string().optional().or(z.literal('')),
  route: z.string().optional().or(z.literal('')),
  frequency: z.string().optional().or(z.literal('')),
  duration: z.string().optional().or(z.literal('')),
  quantity: z.string().optional().or(z.literal('')),
  expiry: z.string().optional().or(z.literal('')),
  comments: z.string().optional().or(z.literal('')),
  number: z.string().refine((val) => {
    if (!val) return false;
    const num = Number(val);
    return !isNaN(num) && parseInt(val) > 0;
  }, 'Must be a positive number'),
  physician_id: z.string().optional().or(z.literal('')),
});
type DrugLabelValues = z.infer<typeof drugLabelSchema>;

const DrugLabel = ({
  patient,
  medication,
  onClose,
}: {
  patient: any;
  medication?: any;
  onClose?: () => void;
}) => {
  const authentication = useAppSelector((state) => state.authentication);
  const clinic = useAppSelector((state) => state.resource.clinic);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const ownClinicId = useAppSelector((state) => state.resource.clinic?.id);
  const isNP = useAppSelector(IsTypeNP);

  const primaryPhysicianId = patient.primary_physician;
  const ownId = authentication.user!.id;
  const defaultPhysicianId = isNP ? ownId : primaryPhysicianId;

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { isValid },
  } = useForm<DrugLabelValues>({
    resolver: zodResolver(drugLabelSchema),
    defaultValues: {
      id: '',
      name: '',
      dosage: '',
      route: '',
      frequency: '',
      duration: '',
      quantity: '',
      expiry: '',
      comments: '',
      number: '1',
      physician_id: defaultPhysicianId || '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (defaultPhysicianId) {
      setValue('physician_id', defaultPhysicianId);
    }
  }, [defaultPhysicianId, setValue]);

  let filteredPhysicians = useMemo(() => {
    let physiciansArray = StaffUtils.filterActiveUsers(physicians);
    return physiciansArray.filter(
      (physician: any) =>
        physician.id === primaryPhysicianId ||
        (isNP && physician.id === ownId) ||
        (physician.clinic_id === ownClinicId &&
          !physician.intern_of &&
          !LAB_ROLES.includes(physician.physician_type)),
    );
  }, [physicians, ownClinicId, primaryPhysicianId, isNP, ownId]);

  useEffect(() => {
    if (medication !== undefined) {
      reset();
      setValue('name', medication.name);

      const dosing_instructions = medication.dosing_instructions || [];
      const { dosage, dosage_unit, frequency, time, time_unit } = dosing_instructions[0] || {};

      if (dosage) {
        let dosageStr = `${dosage} ${dosage_unit}`.trim();
        setValue('dosage', dosageStr);
      }

      if (time) {
        let durationStr = `${time} ${time_unit}`.trim();
        setValue('duration', durationStr);
      }

      if (medication.quantity) {
        let quantity = `${medication.quantity} ${medication.quantity_unit}`.trim();
        setValue('quantity', quantity);
      }

      setValue('frequency', frequency || '');
      setValue('route', medication.route || '');
      setValue('comments', medication.comments || '');
    }
  }, [medication, setValue, reset, defaultPhysicianId]);

  function printLabel(values: DrugLabelValues) {
    let { number, physician_id } = values;

    const physician = physicians[physician_id || ''];

    let count = parseInt(number) || 1;
    for (let i = 0; i < count; i++) {
      Dymo.printDrugLabel(patient, physician, clinic, values);
    }

    if (onClose) onClose();
  }

  return (
    <form
      className="drug-label-form"
      onSubmit={handleSubmit(printLabel)}
      style={{ padding: '0 20px' }}
    >
      <div className="row">
        <DefaultTextField
          name="id"
          control={control}
          label="Medication ID"
          style={{ width: '49%', marginRight: '2%' }}
        />
        <DefaultTextField
          name="name"
          control={control}
          label="Medication Name"
          style={{ width: '49%' }}
        />
        <DefaultTextField
          name="dosage"
          control={control}
          label="Dosage"
          style={{ width: '49%', marginRight: '2%' }}
        />
        <DefaultTextField name="route" control={control} label="Route" style={{ width: '49%' }} />
        <DefaultTextField
          name="frequency"
          control={control}
          label="Frequency"
          style={{ width: '49%', marginRight: '2%' }}
        />
        <DefaultTextField name="duration" control={control} label="Duration" style={{ width: '49%' }} />
        <DefaultTextField
          name="quantity"
          control={control}
          label="Quantity"
          style={{ width: '49%', marginRight: '2%' }}
        />
        <DefaultTextField name="expiry" control={control} label="Expiry" style={{ width: '49%' }} />
        <DefaultTextField
          name="comments"
          control={control}
          label="Comments"
          style={{ width: '100%' }}
        />
        <DefaultTextField
          name="number"
          control={control}
          label="Number of labels to print"
          style={{ width: '49%', marginRight: '2%' }}
        />
        <DefaultSelectField
          name="physician_id"
          control={control}
          label="Practitioner"
          style={{ width: '49%' }}
          inputElements={filteredPhysicians.map((physician: any) => (
            <MenuItem key={physician.id} value={physician.id}>
              {StaffUtils.getName(physician)}
            </MenuItem>
          ))}
        />
      </div>
      <div className="button-wrapper" style={{ textAlign: 'center', marginTop: '20px' }}>
        <DefaultButton name="Print Label" disabled={!isValid} style={{ width: '200px' }} />
      </div>
    </form>
  );
};

export default DrugLabel;
